import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Button } from './ui/button';
import { Search, Loader2, Download, FileSpreadsheet, FileText, Pencil, Landmark } from 'lucide-react';
import { OutcomeBadge } from './AdminDealComplianceTab';
import { openAuthed } from '../lib/authSession';

const TYPE_LABELS = {
  STR: 'STR — Suspicious Transaction',
  LCTR: 'LCTR — Large Cash Transaction',
  TPR: 'TPR — Terrorist Property',
  Other: 'Other',
};

/**
 * FintracRegister — audit register of every FINTRAC report filed in Deal
 * Compliance Reviews, filterable by type / date / search, exportable CSV & PDF.
 */
const FintracRegister = ({ apiUrl, adminPassword, onEdit, refreshKey }) => {
  const [data, setData] = useState({ rows: [], total: 0, by_type: {} });
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [type, setType] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');

  const params = useMemo(() => {
    const p = new URLSearchParams();
    if (type) p.set('report_type', type);
    if (from) p.set('date_from', from);
    if (to) p.set('date_to', to);
    if (q.trim()) p.set('q', q.trim());
    return p;
  }, [type, from, to, q]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(`${apiUrl}/api/admin/deal-compliance/fintrac-register?${params}`, { headers: { 'X-Admin-Password': adminPassword } });
      if (r.ok) setData(await r.json());
    } catch (e) {
      console.error('Failed to load FINTRAC register:', e);
    } finally {
      setLoading(false);
    }
  }, [apiUrl, adminPassword, params]);

  useEffect(() => { load(); }, [load, refreshKey]);

  const exportUrl = (format) => {
    const p = new URLSearchParams(params);
    p.set('format', format);
    return `${apiUrl}/api/admin/deal-compliance/fintrac-register/export?${p}`;
  };

  return (
    <div data-testid="fintrac-register">
      <div className="flex flex-wrap items-center gap-2 mb-3 text-xs">
        <span className="px-2 py-1 rounded bg-amber-100 text-amber-900 font-semibold flex items-center gap-1" data-testid="fintrac-total">
          <Landmark className="h-3 w-3" />{data.total} report{data.total === 1 ? '' : 's'} filed
        </span>
        {Object.entries(data.by_type).map(([t, n]) => (
          <span key={t} className="px-2 py-1 rounded bg-gray-100 text-gray-700" data-testid={`fintrac-count-${t}`}>{t}: {n}</span>
        ))}
        <div className="ml-auto flex items-center gap-2">
          <Button variant="outline" size="sm" asChild>
            <a href={exportUrl('csv')} onClick={openAuthed} data-testid="fintrac-export-csv"><FileSpreadsheet className="h-4 w-4 mr-1" />Export CSV</a>
          </Button>
          <Button variant="outline" size="sm" asChild>
            <a href={exportUrl('pdf')} onClick={openAuthed} target="_blank" rel="noopener noreferrer" data-testid="fintrac-export-pdf"><FileText className="h-4 w-4 mr-1" />Export PDF</a>
          </Button>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2 mb-4">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <input type="text" placeholder="Search client, broker, lender, property…" value={q} onChange={(e) => setQ(e.target.value)}
            className="w-full pl-9 pr-3 py-1.5 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-[#0077B6]" data-testid="fintrac-search" />
        </div>
        <select value={type} onChange={(e) => setType(e.target.value)} className="px-3 py-1.5 text-sm border border-gray-300 rounded-md" data-testid="fintrac-type-filter">
          <option value="">All report types</option>
          {Object.entries(TYPE_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
        <label className="text-xs text-gray-600 flex items-center gap-1">From
          <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="px-2 py-1.5 text-sm border border-gray-300 rounded-md" data-testid="fintrac-date-from" />
        </label>
        <label className="text-xs text-gray-600 flex items-center gap-1">To
          <input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="px-2 py-1.5 text-sm border border-gray-300 rounded-md" data-testid="fintrac-date-to" />
        </label>
      </div>

      {loading ? (
        <div className="py-12 text-center"><Loader2 className="h-6 w-6 animate-spin mx-auto text-gray-400" /></div>
      ) : data.rows.length === 0 ? (
        <p className="py-10 text-center text-sm text-gray-500" data-testid="fintrac-empty">No FINTRAC reports match. Reports appear here when a review has "FINTRAC report filed" ticked.</p>
      ) : (
        <div className="overflow-x-auto border rounded-lg">
          <table className="w-full text-sm">
            <thead className="bg-amber-50 text-xs uppercase text-amber-900">
              <tr>
                <th className="text-left px-3 py-2">Report Date</th>
                <th className="text-left px-3 py-2">Type</th>
                <th className="text-left px-3 py-2">Client / Deal</th>
                <th className="text-left px-3 py-2">Broker</th>
                <th className="text-left px-3 py-2">Lender</th>
                <th className="text-left px-3 py-2">Outcome</th>
                <th className="text-left px-3 py-2">Officer</th>
                <th className="text-left px-3 py-2">FINTRAC Notes</th>
                <th className="text-right px-3 py-2">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {data.rows.map(r => (
                <tr key={r.id} className="hover:bg-gray-50" data-testid={`fintrac-row-${r.id}`}>
                  <td className="px-3 py-2 text-gray-800 whitespace-nowrap">{r.fintrac_report_date || '—'}</td>
                  <td className="px-3 py-2"><span className="px-1.5 py-0.5 rounded bg-amber-100 text-amber-900 text-xs font-semibold">{r.fintrac_report_type || '—'}</span></td>
                  <td className="px-3 py-2">
                    <p className="font-medium text-gray-800">{r.client_names}</p>
                    {r.property_address && <p className="text-xs text-gray-500 truncate max-w-[200px]">{r.property_address}</p>}
                  </td>
                  <td className="px-3 py-2 text-gray-700">{r.broker || '—'}</td>
                  <td className="px-3 py-2 text-gray-700">{r.lender || '—'}</td>
                  <td className="px-3 py-2"><OutcomeBadge outcome={r.outcome} /></td>
                  <td className="px-3 py-2 text-xs text-gray-600">{r.signed_name}<br /><span className="text-gray-400">rev {r.revision}</span></td>
                  <td className="px-3 py-2 text-xs text-gray-700 max-w-[260px]"><p className="line-clamp-3 whitespace-pre-line" title={r.fintrac_notes || ''}>{r.fintrac_notes || '—'}</p></td>
                  <td className="px-3 py-2">
                    <div className="flex items-center justify-end gap-1">
                      <Button variant="outline" size="sm" asChild>
                        <a href={`${apiUrl}/api/admin/deal-compliance/reviews/${r.id}/pdf`} onClick={openAuthed} target="_blank" rel="noopener noreferrer" data-testid={`fintrac-pdf-${r.id}`}>
                          <Download className="h-4 w-4" />
                        </a>
                      </Button>
                      <Button size="sm" className="bg-[#0077B6]" onClick={() => onEdit(r.id)} data-testid={`fintrac-edit-${r.id}`}>
                        <Pencil className="h-4 w-4" />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default FintracRegister;
